import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { useChapters, useCreateChapter } from '@/hooks/useChapters';
import { useProjectMeta } from '@/hooks/meta/useProjectMeta';
import { useChapterBeats, useCreateChapterBeat, useUpdateChapterBeat, useDeleteChapterBeat } from '@/hooks/useChapterBeats';
import { ArrowLeft, ArrowRight, X } from 'lucide-react';
import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { StepIndicator } from './components/step-indicator';
import { ChooseChapterStep } from './components/choose-chapter-step';
import { PlanScenesStep, type SceneCard } from './components/plan-scenes-step';
import { ReviewStep } from './components/review-step';
import { cn } from '@/lib/utils';

const STEPS = [
  { label: 'Choose Chapter' },
  { label: 'Plan Scenes' },
  { label: 'Review & Save' },
];

export default function ChapterScenePlannerPage() {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const { toast } = useToast();
  const { currentStoryId } = useProjectMeta(projectId || null);
  const storyId = currentStoryId?.toString();

  const { data: chapters = [] } = useChapters(storyId, { enabled: !!storyId });
  const createChapterMutation = useCreateChapter();
  const createBeatMutation = useCreateChapterBeat();
  const updateBeatMutation = useUpdateChapterBeat();
  const deleteBeatMutation = useDeleteChapterBeat();

  const [currentStep, setCurrentStep] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  // Step 1 state
  const [selectedChapterId, setSelectedChapterId] = useState<number | null>(null);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [newChapterTitle, setNewChapterTitle] = useState('');
  const [newChapterDescription, setNewChapterDescription] = useState('');

  // Step 2 state
  const [scenes, setScenes] = useState<SceneCard[]>([
    { id: crypto.randomUUID(), title: 'Scene 1', description: '' },
  ]);
  
  const { data: existingBeats } = useChapterBeats(selectedChapterId || undefined);
  const lastLoadedChapterId = useRef<number | null>(null);

  // Load existing beats when a chapter is selected
  useEffect(() => {
    // If it's a new chapter and we haven't reset for it yet
    if (isCreatingNew) {
      if (lastLoadedChapterId.current !== -1) {
        setScenes([
          { id: crypto.randomUUID(), title: 'Scene 1', description: '' },
        ]);
        lastLoadedChapterId.current = -1;
      }
      return;
    }

    // If we've selected an existing chapter and beats have loaded
    if (selectedChapterId && existingBeats && lastLoadedChapterId.current !== selectedChapterId) {
      if (existingBeats.length > 0) {
        setScenes(existingBeats.map(beat => ({
          id: beat.id.toString(),
          title: beat.title,
          description: beat.description || '',
        })));
      } else {
        // No beats yet, show a blank one
        setScenes([
          { id: crypto.randomUUID(), title: 'Scene 1', description: '' },
        ]);
      }
      lastLoadedChapterId.current = selectedChapterId;
    }
  }, [selectedChapterId, existingBeats, isCreatingNew]);

  const getChapterName = useCallback(() => {
    if (isCreatingNew) return newChapterTitle || 'New Chapter';
    const chapter = chapters.find(c => c.id === selectedChapterId);
    return chapter?.title || 'Selected Chapter';
  }, [isCreatingNew, newChapterTitle, chapters, selectedChapterId]);

  const getChapterDescription = useCallback(() => {
    if (isCreatingNew) return newChapterDescription;
    const chapter = chapters.find(c => c.id === selectedChapterId);
    return chapter?.description || undefined;
  }, [isCreatingNew, newChapterDescription, chapters, selectedChapterId]);

  const canProceedStep0 = () => {
    if (isCreatingNew) return newChapterTitle.trim().length > 0;
    return selectedChapterId !== null;
  };

  const canProceedStep1 = () => {
    return scenes.length > 0 && scenes.some(s => s.title.trim().length > 0);
  };

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleExit = () => {
    navigate(`/projects/${projectId}/chapters`);
  };

  const handleSave = async () => {
    if (!projectId || !storyId) return;
    setIsSaving(true);

    try {
      let chapterId = selectedChapterId;

      // Create chapter if needed
      if (isCreatingNew) {
        const newChapter = await createChapterMutation.mutateAsync({
          storyId: parseInt(storyId),
          title: newChapterTitle,
          description: newChapterDescription,
          projectId,
        });
        chapterId = newChapter.id;
      }

      if (!chapterId) {
        throw new Error('No chapter selected');
      }

      // 1. Prepare sync: identify what to create, update, and delete
      const currentBeatIds = scenes.map(s => Number(s.id)).filter(id => !isNaN(id));
      const beatsToDelete = existingBeats?.filter(eb => !currentBeatIds.includes(eb.id)) || [];

      // 2. Perform deletions
      for (const beat of beatsToDelete) {
        await deleteBeatMutation.mutateAsync({ beatId: beat.id, chapterId: chapterId as number });
      }

      // 3. Perform creations and updates
      for (const scene of scenes) {
        if (!scene.title.trim()) continue;

        const isExisting = !isNaN(Number(scene.id));
        
        if (isExisting) {
          await updateBeatMutation.mutateAsync({
            beatId: Number(scene.id),
            chapterId: chapterId as number,
            updates: {
              title: scene.title,
              description: scene.description,
            }
          });
        } else {
          await createBeatMutation.mutateAsync({
            chapterId: chapterId as number,
            title: scene.title,
            description: scene.description,
          });
        }
      }

      toast({
        title: 'Scenes Saved!',
        description: `${scenes.length} scenes planned for ${getChapterName()}.`,
      });

      // Navigate to the chapter editor
      navigate(`/projects/${projectId}/chapters/${chapterId}`);
    } catch (error) {
      console.error('Error saving scene plan:', error);
      toast({
        title: 'Error',
        description: 'Failed to save scene plan. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };



  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <ChooseChapterStep
            chapters={chapters.map(c => ({ id: c.id, title: c.title, description: c.description }))}
            selectedChapterId={selectedChapterId}
            onSelectChapter={setSelectedChapterId}
            isCreatingNew={isCreatingNew}
            onToggleCreateNew={() => {
              setIsCreatingNew(!isCreatingNew);
              setSelectedChapterId(null);
            }}
            newChapterTitle={newChapterTitle}
            onNewChapterTitleChange={setNewChapterTitle}
            newChapterDescription={newChapterDescription}
            onNewChapterDescriptionChange={setNewChapterDescription}
          />
        );
      case 1:
        return (
          <PlanScenesStep
            scenes={scenes}
            onScenesChange={setScenes}
            chapterName={getChapterName()}
            chapterDescription={getChapterDescription()}
            chapterId={selectedChapterId || undefined}
            storyId={storyId}
            projectId={projectId}
          />
        );
      case 2:
        return (
          <ReviewStep
            chapterName={getChapterName()}
            chapterDescription={getChapterDescription()}
            scenes={scenes}
            onSave={handleSave}
            isSaving={isSaving}
            projectId={projectId}
            storyId={storyId}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col flex-1 w-full overflow-hidden bg-background">
      {/* Top Bar */}
      <div className="flex-shrink-0 border-b border-border/60 bg-background/80 backdrop-blur-sm z-10">
        <div className="w-full max-w-7xl mx-auto px-8 py-3 flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleExit}
            className="text-xs text-muted-foreground hover:text-foreground gap-1.5 h-8"
          >
            <X className="h-3.5 w-3.5" />
            Exit
          </Button>

          <StepIndicator steps={STEPS} currentStep={currentStep} />

          <div className="w-16" /> {/* Spacer for centering */}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto min-h-0 scrollbar-hide flex flex-col">
        <div className={cn(
          "w-full max-w-7xl mx-auto px-8 py-6 flex-1 flex flex-col",
          currentStep === 0 && "pt-24 pb-12"
        )}>
          {renderStep()}
        </div>
      </div>

      {/* Bottom Navigation - not shown on last step since it has its own buttons */}
      {currentStep < 2 && (
        <div className="flex-shrink-0 border-t border-border/60 bg-background/80 backdrop-blur-sm">
          <div className="w-full max-w-7xl mx-auto px-8 py-4 flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={handleBack}
              disabled={currentStep === 0}
              className="h-10 rounded-xl text-sm gap-2 text-muted-foreground"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back
            </Button>
            <Button
              onClick={handleNext}
              disabled={currentStep === 0 ? !canProceedStep0() : !canProceedStep1()}
              className="h-10 rounded-xl text-sm gap-2 font-medium shadow-sm"
            >
              Continue
              <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
